import React, { useCallback, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { QROptions, QRType, downloadPngAtSize, downloadSvg, downloadPdf } from '../lib/qrUtils';
import { Check, ChevronDown, Copy, Download, Loader2, RotateCcw } from 'lucide-react';

interface DownloadButtonProps {
  qrDataUrl: string;
  svg: string;
  type: QRType;
  options: QROptions;
  disabled: boolean;
  content?: string; // Raw content string for "Copy Data"
}

type DownloadItem = {
  label: string;
  key: string;
  recommended?: boolean;
  action: () => Promise<void>;
};

export function DownloadButton({
  qrDataUrl,
  svg,
  type,
  options,
  disabled,
  content,
}: DownloadButtonProps) {
  const { t } = useI18n();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const typeLabel = t(`qr.types.${type}`);
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const baseName = t('qr.download.filename')
    .replace('{type}', type)
    .replace('{date}', dateStr);

  const handleDownload = useCallback(async (key: string, action: () => Promise<void>) => {
    setLoadingKey(key);
    setDropdownOpen(false);
    try {
      await action();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 1000);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setLoadingKey(null);
    }
  }, []);

  const downloadItems: DownloadItem[] = [
    {
      label: t('qr.download.png512'),
      key: 'png512',
      action: async () => {
        await downloadPngAtSize(qrDataUrl, 512, `${baseName}-512.png`, options.logo, options);
      },
    },
    {
      label: t('qr.download.png1024'),
      key: 'png1024',
      recommended: true,
      action: async () => {
        await downloadPngAtSize(qrDataUrl, 1024, `${baseName}-1024.png`, options.logo, options);
      },
    },
    {
      label: t('qr.download.png2048'),
      key: 'png2048',
      action: async () => {
        await downloadPngAtSize(qrDataUrl, 2048, `${baseName}-2048.png`, options.logo, options);
      },
    },
    {
      label: t('qr.download.svg'),
      key: 'svg',
      action: async () => {
        downloadSvg(svg, `${baseName}.svg`);
      },
    },
    {
      label: t('qr.download.pdf'),
      key: 'pdf',
      action: async () => {
        await downloadPdf(qrDataUrl, `${baseName}.pdf`, typeLabel, options.logo, options);
      },
    },
  ];

  const handleCopyData = useCallback(async () => {
    const textToCopy = content || qrDataUrl;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 1000);
    } catch {
      // fallback
    }
  }, [content, qrDataUrl]);

  const handleReset = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, []);

  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const isLoading = loadingKey !== null;

  return (
    <div className="space-y-3">
      {/* Main Download Button */}
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          onClick={() => setDropdownOpen((prev) => !prev)}
          disabled={disabled || isLoading}
          className={`
            w-full py-3 px-6 rounded-full font-semibold text-base
            flex items-center justify-center gap-2 transition-all
            ${
              success
                ? 'bg-green-500 text-white'
                : disabled
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90'
            }
          `}
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : success ? (
            <Check className="w-5 h-5" />
          ) : (
            <Download className="w-5 h-5" />
          )}
          <span>
            {success
              ? t('actions.copied')
              : isLoading
              ? t('actions.download')
              : t('qr.download.title')}
          </span>
          {!isLoading && !success && (
            <ChevronDown
              className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          )}
        </button>

        {/* Dropdown Menu */}
        {dropdownOpen && !disabled && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-border overflow-hidden z-50">
            {downloadItems.map((item) => (
              <button
                key={item.key}
                onClick={() => handleDownload(item.key, item.action)}
                disabled={isLoading}
                className={`
                  w-full text-left px-4 py-3 text-sm transition-colors flex items-center gap-2
                  ${
                    item.recommended
                      ? 'border-l-4 border-l-green-500 bg-green-50/50 text-green-700 font-medium'
                      : 'border-l-4 border-l-transparent text-foreground hover:bg-muted'
                  }
                  ${isLoading && loadingKey === item.key ? 'opacity-70' : ''}
                `}
              >
                {loadingKey === item.key ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 text-muted-foreground" />
                )}
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Secondary Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleCopyData}
          disabled={disabled}
          className="
            flex-1 py-2 px-4 rounded-lg text-sm font-medium
            border border-border bg-background text-foreground
            hover:bg-muted transition-colors
            flex items-center justify-center gap-2
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          <Copy className="w-4 h-4" />
          {t('qr.actions.copyData')}
        </button>
        <button
          onClick={handleReset}
          className="
            flex-1 py-2 px-4 rounded-lg text-sm font-medium
            text-muted-foreground hover:text-foreground hover:bg-muted
            transition-colors
            flex items-center justify-center gap-2
          "
        >
          <RotateCcw className="w-4 h-4" />
          {t('qr.actions.resetConfig')}
        </button>
      </div>
    </div>
  );
}
